import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { PageEvent } from '@angular/material/paginator';
import { MatOption, MatSelect } from '@angular/material/select';
import { Sort } from '@angular/material/sort';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  forkJoin,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';
import { AdminPanel } from '../../core/interfaces';
import { TSP } from '../../core/models';
import { CityService } from '../../core/services/city/city.service';
import { ConfirmationDialog } from '../../shared/components/confirmation-dialog/confirmation-dialog';
import { EntityListComponent } from '../../shared/components/entity-list/entity-list.component';
import { CityForm, CityFormData } from './city-form/city-form';
import {
  CityReplaceDialog,
  CityReplaceDialogData,
  CityReplaceDialogResult,
} from './city-replace-dialog/city-replace-dialog';

@Component({
  selector: 'app-cities',
  imports: [
    CommonModule,
    EntityListComponent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatCheckboxModule,
  ],
  templateUrl: './cities.component.html',
  styleUrl: './cities.component.scss',
})
export class CitiesComponent implements OnInit, OnDestroy {
  private readonly cityService = inject(CityService);
  private readonly dialog = inject(MatDialog);

  public readonly countries$ = this.cityService.getCountries().pipe(
    catchError(() => of([])),
    shareReplay(1),
  );
  public readonly regions$ = this.cityService.getRegions().pipe(
    catchError(() => of([])),
    shareReplay(1),
  );

  public filteredRegions$: Observable<TSP.Internal.Dictionary.Region.Item[]> = of([]);

  public countryFilter = new FormControl<number | null>(null);
  public regionFilter = new FormControl<number | null>(null);

  public columns: AdminPanel.Entity.EntityColumn[] = [
    { key: 'id', label: 'ID', sortable: true, sortKey: 'id', type: 'number' },
    { key: 'enName', label: 'English', sortable: false, type: 'text' },
    { key: 'ruName', label: 'Russian', sortable: false, type: 'text' },
    { key: 'ukName', label: 'Ukrainian', sortable: false, type: 'text' },
    { key: 'synonyms', label: 'Синоніми', sortable: false, type: 'array' },
    { key: 'regionName', label: 'Регіон', sortable: true, sortKey: 'regionId', type: 'text' },
    { key: 'countryName', label: 'Країна', sortable: true, sortKey: 'countryId', type: 'text' },
    {
      key: 'profilesCount',
      label: 'Кандидати',
      sortable: false,
      type: 'count-icon',
      iconWhenEmpty: 'person_off',
      iconWhenFilled: 'person',
    },
    {
      key: 'projectsCount',
      label: 'Проекти',
      sortable: false,
      type: 'count-icon',
      iconWhenEmpty: 'folder_off',
      iconWhenFilled: 'folder',
    },
    {
      key: 'deletedProfilesCount',
      label: 'Видалені кандидати',
      sortable: false,
      type: 'count-icon',
      iconWhenEmpty: 'person_remove',
      iconWhenFilled: 'person_remove',
    },
  ];

  public cities$: Observable<TSP.Internal.Dictionary.City.Details[]> = of([]);
  public totalItems$: Observable<number> = of(0);
  public loading = false;
  public pageSize$: Observable<number> = of(50);
  public pageIndex$: Observable<number> = of(0);

  #currentCities: TSP.Internal.Dictionary.City.Details[] = [];
  #sortBy: BehaviorSubject<string> = new BehaviorSubject('id');
  #sortOrder: BehaviorSubject<'asc' | 'desc'> = new BehaviorSubject('asc');
  #search: BehaviorSubject<string> = new BehaviorSubject('');
  #pageSize: BehaviorSubject<number> = new BehaviorSubject(50);
  #pageIndex: BehaviorSubject<number> = new BehaviorSubject(0);
  #countryId: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);
  #regionId: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);
  #showEmptyCities: BehaviorSubject<boolean> = new BehaviorSubject(false);
  #showDuplicates: BehaviorSubject<boolean> = new BehaviorSubject(false);
  #showWithoutRegion: BehaviorSubject<boolean> = new BehaviorSubject(false);
  #dateTime: BehaviorSubject<number> = new BehaviorSubject(0);
  #ngUnsubscribe: Subject<void> = new Subject<void>();

  /** Left-side action buttons: merge (if duplicate) + fill from maps */
  public readonly customRowActions = (item: AdminPanel.Entity.EntityType): AdminPanel.Entity.EntityRowAction[] => {
    const city = item as TSP.Internal.Dictionary.City.Details;
    const actions: AdminPanel.Entity.EntityRowAction[] = [];

    if (this.#attachedCount(city) > 0) {
      const duplicate = this.#findDuplicate(city);
      if (duplicate) {
        actions.push({
          type: 'replace-to-duplicate',
          icon: 'merge',
          tooltip: `Перенести до дубліката: ${duplicate.enName} / ${duplicate.ukName}`,
          color: 'accent',
        });
      }
    }

    actions.push({ type: 'fill-from-maps', icon: 'my_location', tooltip: 'Заповнити з Google Maps', color: 'primary' });

    return actions;
  };

  /** Rightmost slot (mutual exclusive with delete): replace when city has attached data */
  public readonly endRowActions = (item: AdminPanel.Entity.EntityType): AdminPanel.Entity.EntityRowAction[] => {
    const city = item as TSP.Internal.Dictionary.City.Details;
    if (this.#attachedCount(city) > 0) {
      return [{ type: 'replace', icon: 'swap_horiz', tooltip: 'Перенести до іншого міста', color: 'accent' }];
    }
    return [];
  };

  /**
   * Show delete only when nothing holds the city. Deleted candidates count too: they are hidden from
   * recruiters, but the profile foreign key still refuses the delete, so such a city has to be replaced.
   */
  public readonly showDelete = (item: AdminPanel.Entity.EntityType): boolean => {
    const city = item as TSP.Internal.Dictionary.City.Details;
    return this.#attachedCount(city) === 0;
  };

  /** Gray out rows for cities with no attached data */
  public readonly rowClass = (item: AdminPanel.Entity.EntityType): string => {
    const city = item as TSP.Internal.Dictionary.City.Details;
    return this.#attachedCount(city) === 0 ? 'row--muted' : '';
  };

  public ngOnInit(): void {
    this.loadData();
  }

  public ngOnDestroy(): void {
    this.#ngUnsubscribe.next();
    this.#ngUnsubscribe.complete();
  }

  public loadData(): void {
    const search$ = this.#search.asObservable().pipe(distinctUntilChanged());
    const dateTime$ = this.#dateTime.asObservable().pipe(distinctUntilChanged());
    const countryId$ = this.#countryId.asObservable().pipe(distinctUntilChanged());
    const regionId$ = this.#regionId.asObservable().pipe(distinctUntilChanged());

    this.pageIndex$ = this.#pageIndex.asObservable().pipe(distinctUntilChanged());
    this.pageSize$ = this.#pageSize.asObservable().pipe(distinctUntilChanged());

    this.filteredRegions$ = combineLatest([
      this.regions$,
      this.countryFilter.valueChanges.pipe(startWith(null as number | null)),
    ]).pipe(
      map(([regions, countryId]) => (countryId ? regions.filter((region) => region.countryId === countryId) : regions)),
    );

    const showEmptyCities$ = this.#showEmptyCities.asObservable().pipe(distinctUntilChanged());
    const showDuplicates$ = this.#showDuplicates.asObservable().pipe(distinctUntilChanged());
    const showWithoutRegion$ = this.#showWithoutRegion.asObservable().pipe(distinctUntilChanged());
    const sortBy$ = this.#sortBy.asObservable().pipe(distinctUntilChanged());
    const sortOrder$ = this.#sortOrder.asObservable().pipe(distinctUntilChanged());

    const data$ = combineLatest([
      this.pageIndex$,
      this.pageSize$,
      search$,
      dateTime$,
      countryId$,
      regionId$,
      showEmptyCities$,
      showDuplicates$,
      showWithoutRegion$,
      sortBy$,
      sortOrder$,
    ]).pipe(
      tap(() => (this.loading = true)),
      switchMap(
        ([
          pageIndex,
          pageSize,
          search,
          ,
          countryId,
          regionId,
          showEmptyCities,
          showDuplicates,
          showWithoutRegion,
          sortBy,
          sortOrder,
        ]) =>
          this.cityService.getCitiesList({
            page: pageIndex + 1,
            size: pageSize,
            search: search || undefined,
            countryId: countryId ?? undefined,
            regionId: regionId ?? undefined,
            showEmptyCities: showEmptyCities || undefined,
            showDuplicates: showDuplicates || undefined,
            showWithoutRegion: showWithoutRegion || undefined,
            sortBy: sortBy,
            sortOrder: sortOrder.toUpperCase() as 'ASC' | 'DESC',
          }),
      ),
      tap((response) => {
        this.loading = false;
        this.#currentCities = response.items;
      }),
      catchError((error) => {
        this.loading = false;
        return throwError(() => error);
      }),
      shareReplay(1),
      takeUntil(this.#ngUnsubscribe),
    );

    this.cities$ = data$.pipe(map((response) => response.items));
    this.totalItems$ = data$.pipe(map((response) => response.totalCount));
  }

  public onPageChange(event: PageEvent): void {
    this.#pageIndex.next(event.pageIndex);
    this.#pageSize.next(event.pageSize);
  }

  public onSortChange(sort: Sort): void {
    this.#pageIndex.next(0);
    this.#sortBy.next(sort.active);
    this.#sortOrder.next((sort.direction || 'asc') as 'asc' | 'desc');
  }

  public onSearch(searchTerm: string): void {
    this.#pageIndex.next(0);
    this.#search.next(searchTerm);
  }

  public onCountryFilter(countryId: number | null): void {
    this.#pageIndex.next(0);
    this.#countryId.next(countryId);
    this.regionFilter.setValue(null, { emitEvent: false });
    this.#regionId.next(null);
  }

  public onRegionFilter(regionId: number | null): void {
    this.#pageIndex.next(0);
    this.#regionId.next(regionId);
  }

  public onShowEmptyCitiesChange(checked: boolean): void {
    this.#pageIndex.next(0);
    this.#showEmptyCities.next(checked);
  }

  public onShowDuplicatesChange(checked: boolean): void {
    this.#pageIndex.next(0);
    this.#showDuplicates.next(checked);
  }

  public onShowWithoutRegionChange(checked: boolean): void {
    this.#pageIndex.next(0);
    this.#showWithoutRegion.next(checked);
  }

  public onAction(action: AdminPanel.Entity.EntityAction): void {
    switch (action.type) {
      case 'create':
        this.#openCityForm(undefined);
        break;
      case 'edit':
        if (action.item) {
          this.#openCityForm(action.item as TSP.Internal.Dictionary.City.Details);
        }
        break;
      case 'delete':
        if (action.item) {
          this.#deleteCity(action.item as TSP.Internal.Dictionary.City.Details);
        }
        break;
      case 'replace':
        if (action.item) {
          this.#replaceCity(action.item as TSP.Internal.Dictionary.City.Details);
        }
        break;
      case 'replace-to-duplicate':
        if (action.item) {
          this.#replaceToDuplicate(action.item as TSP.Internal.Dictionary.City.Details);
        }
        break;
      case 'fill-from-maps':
        if (action.item) {
          this.#fillFromMaps(action.item as TSP.Internal.Dictionary.City.Details);
        }
        break;
    }
  }

  #openCityForm(city: TSP.Internal.Dictionary.City.Details | undefined): void {
    forkJoin({ countries: this.countries$, regions: this.regions$ })
      .pipe(
        takeUntil(this.#ngUnsubscribe),
        switchMap(({ countries, regions }) =>
          this.dialog
            .open(CityForm, {
              maxWidth: '760px',
              width: '720px',
              data: { city, countries, regions } satisfies CityFormData,
            })
            .afterClosed(),
        ),
        filter(Boolean),
        switchMap(
          (result: {
            type: 'create' | 'update';
            payload: TSP.Internal.Dictionary.City.CreatePayload | TSP.Internal.Dictionary.City.UpdatePayload;
          }) =>
            result.type === 'create'
              ? this.cityService.createCity(result.payload as TSP.Internal.Dictionary.City.CreatePayload)
              : this.cityService.updateCity(result.payload as TSP.Internal.Dictionary.City.UpdatePayload),
        ),
        tap(() => this.#dateTime.next(Date.now())),
      )
      .subscribe();
  }

  #deleteCity(city: TSP.Internal.Dictionary.City.Details): void {
    this.dialog
      .open(ConfirmationDialog, {
        data: { text: `Видалити місто "${city.enName}"? Цю дію неможливо скасувати.` },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.cityService.deleteCity(city.id)),
        tap(() => this.#dateTime.next(Date.now())),
        takeUntil(this.#ngUnsubscribe),
      )
      .subscribe();
  }

  #replaceCity(city: TSP.Internal.Dictionary.City.Details): void {
    this.dialog
      .open(CityReplaceDialog, {
        maxWidth: '560px',
        width: '520px',
        data: { city } satisfies CityReplaceDialogData,
      })
      .afterClosed()
      .pipe(
        filter((result): result is CityReplaceDialogResult => !!result),
        switchMap((result) => this.cityService.replaceCity({ existedItemId: city.id, newItemId: result.targetCityId })),
        tap(() => this.#dateTime.next(Date.now())),
        takeUntil(this.#ngUnsubscribe),
      )
      .subscribe();
  }

  #replaceToDuplicate(city: TSP.Internal.Dictionary.City.Details): void {
    const duplicate = this.#findDuplicate(city);
    if (!duplicate) return;

    this.dialog
      .open(ConfirmationDialog, {
        data: {
          text: `Перенести всі дані з "${city.enName}" до дубліката "${duplicate.enName}"? Цю дію неможливо скасувати.`,
        },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.cityService.replaceCity({ existedItemId: city.id, newItemId: duplicate.id })),
        tap(() => this.#dateTime.next(Date.now())),
        takeUntil(this.#ngUnsubscribe),
      )
      .subscribe();
  }

  #fillFromMaps(city: TSP.Internal.Dictionary.City.Details): void {
    this.dialog
      .open(ConfirmationDialog, {
        data: { text: `Заповнити дані міста "${city.enName}" з Google Maps? Поточні дані можуть бути оновлені.` },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.cityService.fillFromGooglePlaces(city.id)),
        tap(() => this.#dateTime.next(Date.now())),
        takeUntil(this.#ngUnsubscribe),
      )
      .subscribe();
  }

  #attachedCount(city: TSP.Internal.Dictionary.City.Details): number {
    return city.profilesCount + city.projectsCount + (city.deletedProfilesCount ?? 0);
  }

  #findDuplicate(city: TSP.Internal.Dictionary.City.Details): TSP.Internal.Dictionary.City.Details | null {
    if (!city.synonymsAndTranslations?.length) return null;
    return (
      this.#currentCities.find(
        (other) =>
          other.id !== city.id && other.synonymsAndTranslations?.some((s) => city.synonymsAndTranslations.includes(s)),
      ) ?? null
    );
  }
}
